import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, MapPin, ExternalLink, Ban } from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt, enUS, es, fr } from "date-fns/locale";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { getTranslatedValue } from "@/components/TranslatableField";
import { BACKEND_URL } from "../config";

const DATE_LOCALES = { pt, en: enUS, es, fr };

export const resolveEventImage = (url) => {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
};

export const formatEventDates = (start, end, locale) => {
  if (!start) return null;
  try {
    const s = parseISO(start);
    if (end && end !== start) {
      const e = parseISO(end);
      const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
      if (sameMonth) return `${format(s, "d")}–${format(e, "d MMM yyyy", { locale })}`;
      return `${format(s, "d MMM", { locale })} – ${format(e, "d MMM yyyy", { locale })}`;
    }
    return format(s, "d MMM yyyy", { locale });
  } catch {
    return start;
  }
};

export function EventCard({ event, onOpen }) {
  const { t, i18n } = useTranslation(["comunidade", "common"]);
  const lang = i18n.language?.split("-")[0] || "pt";
  const locale = DATE_LOCALES[lang] || pt;

  const title = getTranslatedValue(event.title, lang) || "Evento";
  const clubName = event.club_name ? getTranslatedValue(event.club_name, lang) : "";
  const img = useMemo(() => resolveEventImage(event.photo), [event.photo]);
  const dateLabel = formatEventDates(event.start_date, event.end_date, locale);
  const isPast = event.status === "inactive";

  return (
    <Card
      className="group bg-card/80 backdrop-blur-sm border-border rounded-xl overflow-hidden cursor-pointer hover:border-primary/60 transition-colors"
      onClick={onOpen}
      data-testid={`event-card-${event.event_id}`}
      title={title}
    >
      <div className="relative aspect-[4/5] bg-muted/40 overflow-hidden">
        {img ? (
          <>
            <img src={img} aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40" />
            <img
              src={img}
              alt={title}
              loading="lazy"
              className="relative w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CalendarDays className="h-10 w-10 text-muted-foreground/50" />
          </div>
        )}
        {dateLabel && (
          <div className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-primary text-primary-foreground shadow-lg">
            <CalendarDays className="h-3.5 w-3.5" />
            {dateLabel}
          </div>
        )}
        {isPast && (
          <div className="absolute top-2 right-2">
            <Badge variant="outline" className="bg-zinc-800/90 text-zinc-300 border-zinc-600 gap-1 text-[10px]">
              <Ban className="h-2.5 w-2.5" /> {t("comunidade:events_section.cancelled", "Terminado")}
            </Badge>
          </div>
        )}
      </div>
      <CardContent className="p-3">
        <p className="font-semibold text-sm truncate flex items-center gap-1.5" data-testid={`event-title-${event.event_id}`}>
          <span className="truncate">{title}</span>
          <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        </p>
        {event.location?.address && (
          <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1 truncate max-w-full">
            <MapPin className="h-3 w-3 flex-shrink-0" /> <span className="truncate">{event.location.address}</span>
          </p>
        )}
        {clubName && (
          <div className="flex items-center gap-1.5 mt-2 min-w-0" title={clubName}>
            <Avatar className="h-5 w-5">
              <AvatarImage src={resolveEventImage(event.club_logo) || undefined} alt={clubName} referrerPolicy="no-referrer" />
              <AvatarFallback className="text-[9px] bg-primary/20">{clubName.slice(0, 1).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground truncate">{clubName}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default EventCard;
